import { useQuery } from '@apollo/client/react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate, useParams } from 'react-router-dom'
import Spinner from '~/components/Spinner'
import { Button } from '~/components/ui/button'
import { GQL_MY_SESSIONS } from '~/gql/sessions/sessions'
import { useSurveyBeforeCertificate } from '~/hooks/use-survey-before-certificate'
import PathDocuments from '~/pages/scorm/components/documents/PathDocuments'
import LearnerFrame from '~/pages/scorm/components/LearnerFrame'
import PositioningPrompt from '~/pages/scorm/components/positioning/PositioningPrompt'
import SurveyDialog from '~/pages/scorm/components/survey/SurveyDialog'
import SessionPathView from '~/pages/scorm/SessionPathView'

/**
 * L'écran d'un parcours (#74).
 *
 * Un parcours a désormais son écran, là où ses étapes s'entassaient dans une
 * carte d'une grille à deux colonnes. La donnée n'a pas changé — `unlocked`,
 * `lockReason` et `unlockAt` étaient déjà calculés et déjà servis — il lui
 * manquait la place d'être lue.
 *
 * ⚠️ La query est `mySessions`, la même que l'accueil, et le parcours est
 * retrouvé par son id côté client. C'est délibéré : un apprenant a quelques
 * formations, pas mille ; la réponse est déjà en cache au moment où il clique,
 * donc l'écran s'ouvre sans attendre, et aucune query dédiée n'est à écrire ni à
 * maintenir. Le jour où un apprenant en aurait des dizaines, une query par id
 * sera le bon moment — pas avant.
 */
function SessionPath() {
  const { t } = useTranslation()
  const { sessionId } = useParams()
  const navigate = useNavigate()
  const { data, loading } = useQuery(GQL_MY_SESSIONS, { fetchPolicy: 'cache-and-network' })
  // Le questionnaire de fin de parcours, proposé avant l'attestation (#320).
  const { request, finish, pending, downloadingId } = useSurveyBeforeCertificate()

  const session = data?.mySessions?.find(s => s.id === sessionId)

  if (loading && !session) {
    return (
      <LearnerFrame label={t('home.title')}>
        <div className="flex justify-center p-16">
          <Spinner className="size-6 text-muted-foreground" />
        </div>
      </LearnerFrame>
    )
  }

  if (!session) {
    return (
      <LearnerFrame label={t('home.title')}>
        <div className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-border p-12 text-center">
          <p className="font-medium">{t('path.notFound')}</p>
          <Button asChild variant="outline">
            <Link to="/my-sessions">{t('path.back')}</Link>
          </Button>
        </div>
      </LearnerFrame>
    )
  }

  return (
    <LearnerFrame label={session.name}>
      <SessionPathView
        session={session}
        onLaunch={moduleId => navigate(`/scorm/${session.id}/${moduleId}`)}
        onDownloadCertificate={() => void request(session.id)}
        downloadingCertificate={downloadingId === session.id}
        documents={<PathDocuments documents={session.documents ?? []} />}
        positioning={<PositioningPrompt session={session} />}
      />
      {pending && <SurveyDialog sessionId={pending.sessionId} survey={pending.survey} onDone={() => void finish()} />}
    </LearnerFrame>
  )
}

export default SessionPath
